import { Link } from "@tanstack/react-router";
import { ArrowRight, MapPin } from "lucide-react";
import { CITY_PAGES, type CityPage } from "@/lib/citiesData";
import { isSuppressedCombo } from "@/lib/cityServiceMatrix";
import type { ArticleCategory } from "@/lib/learningHub";

/**
 * Kyle Roof-style internal linking block.
 *
 * Renders keyword-rich anchor links from a learning-hub article to the
 * matching city × service "money pages" (e.g. /colorado-springs-seo).
 *
 * Anchor text follows the pattern that wins for local SEO:
 *   "{service} in {city}" / "{city} {service}" / "{service} services in {city}"
 *
 * Selection rules:
 *  - Pick the service that matches the article's category (SEO, Web Design,
 *    Google Maps). Falls back to SEO when no match.
 *  - Only show cities that actually have a built city × service money page
 *    (currently Colorado Springs). When more cities ship, add them to
 *    LIVE_CITY_SERVICE_PAGES below.
 */

// 2026-04: dropped "google-maps" as a city money page. The /{city}-local-seo
// route was retired. Articles previously routed there (Google Maps, Reviews &
// Reputation) now land on the city's SEO page, which already covers local
// search + Map Pack work.
type MoneyService = "seo" | "web-design";

const CATEGORY_TO_SERVICE: Partial<Record<ArticleCategory, MoneyService>> = {
  SEO: "seo",
  "Web Design": "web-design",
  "Google Maps": "seo",
  "AI & GEO": "seo",
  "Reviews & Reputation": "seo",
  "Marketing Strategy": "seo",
  "Buyer's Guide": "seo",
  "Pricing & Budget": "seo",
};

/**
 * Live city × service money pages. Suppressed combos are filtered out so we
 * never auto-generate a link to a retired spoke from a hub article.
 */
const LIVE_CITY_SERVICE_PAGES: Record<MoneyService, string[]> = {
  seo: CITY_PAGES.map((c) => c.slug).filter((s) => !isSuppressedCombo(s, "seo")),
  "web-design": CITY_PAGES.map((c) => c.slug).filter((s) => !isSuppressedCombo(s, "web-design")),
};

const SERVICE_LABELS: Record<MoneyService, { short: string; full: string }> = {
  seo: { short: "SEO", full: "local SEO services" },
  "web-design": { short: "web design", full: "custom web design services" },
};

/**
 * Generate keyword-rich anchor text variants. Index 0 is preferred but we
 * cycle so the same article doesn't repeat identical anchors.
 */
function anchorVariants(city: string, svc: MoneyService): string[] {
  const labels = SERVICE_LABELS[svc];
  switch (svc) {
    case "seo":
      return [
        `${city} SEO services`,
        `local SEO in ${city}`,
        `${city} local SEO company`,
      ];
    case "web-design":
      return [
        `${city} web design`,
        `custom web design in ${city}`,
        `${city} website design services`,
      ];
    default:
      return [`${labels.full} in ${city}`];
  }
}

export function MoneyPageLinks({
  category,
  className,
}: {
  category: ArticleCategory;
  className?: string;
}) {
  const service = CATEGORY_TO_SERVICE[category] ?? "seo";
  const liveSlugs = LIVE_CITY_SERVICE_PAGES[service];
  if (!liveSlugs || liveSlugs.length === 0) return null;

  const cities: CityPage[] = liveSlugs
    .map((slug) => CITY_PAGES.find((c) => c.slug === slug))
    .filter((c): c is CityPage => Boolean(c));

  if (cities.length === 0) return null;

  const labels = SERVICE_LABELS[service];

  return (
    <aside
      aria-label={`Get ${labels.short} in your Colorado city`}
      className={`mt-12 rounded-2xl border border-brand-gold/30 bg-card p-6 shadow-card sm:p-8 ${className ?? ""}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
            Need help in your city?
          </div>
          <h3 className="mt-1.5 text-xl font-extrabold text-foreground sm:text-2xl">
            Get {labels.full} in Colorado
          </h3>
        </div>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-foreground/80">
        We&apos;re a Colorado-based team. Pick your city to see how we approach{" "}
        <span className="font-semibold text-foreground">{labels.short}</span> for local businesses
        like yours.
      </p>

      <ul className="mt-5 grid gap-3 sm:grid-cols-2">
        {cities.map((city, idx) => {
          const variants = anchorVariants(city.name, service);
          // Rotate anchor variants across cities so anchors stay diverse.
          const anchor = variants[idx % variants.length];
          return (
            <li key={city.slug}>
              <a
                href={`/${city.slug}-${service}`}
                className="group flex items-start gap-3 rounded-xl border border-border bg-background p-4 transition-smooth hover:border-brand-gold/60 hover:bg-brand-gold/5"
              >
                <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-md bg-brand-gold/10 text-brand-gold-deep">
                  <MapPin className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold text-foreground group-hover:text-brand-gold-deep">
                    {anchor}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    /{city.slug}-{service}
                  </span>
                </span>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-brand-gold-deep transition-transform group-hover:translate-x-1" />
              </a>
            </li>
          );
        })}
      </ul>

      <Link
        to="/service-areas"
        className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-brand-gold-deep hover:underline"
      >
        See every Colorado city we serve
        <ArrowRight className="h-4 w-4" />
      </Link>
    </aside>
  );
}
